import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface SessionTypeProps {
  icon: string; label: string; duration: string; frequency: string; format: string;
}

function SessionTypeCard({ icon, label, duration, frequency, format }: SessionTypeProps) {
  return (
    <div className="bg-canvas border border-sage/20 rounded-2xl p-7 hover:border-sage/50 transition-all duration-200">
      <div className="w-9 h-9 flex items-center justify-center text-sage mb-5">
        <i className={`${icon} text-xl`} />
      </div>
      <h3 className="font-inter font-semibold text-sm text-charcoal mb-4">{label}</h3>
      <ul className="flex flex-col gap-2.5">
        {[{ icon: 'ri-time-line', text: duration }, { icon: 'ri-calendar-line', text: frequency }, { icon: 'ri-map-pin-line', text: format }].map((row) => (
          <li key={row.text} className="flex items-center gap-2.5 text-xs text-text-muted font-inter">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`${row.icon} text-sage text-sm`} />
            </span>
            {row.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FeesSection() {
  const { t } = useTranslation();
  const sessionTypes = [
    { icon: 'ri-user-heart-line', labelKey: 'services.fees.type1Label', durationKey: 'services.fees.type1Duration', frequencyKey: 'services.fees.type1Frequency', formatKey: 'services.fees.type1Format' },
    { icon: 'ri-group-2-line',    labelKey: 'services.fees.type2Label', durationKey: 'services.fees.type2Duration', frequencyKey: 'services.fees.type2Frequency', formatKey: 'services.fees.type2Format' },
    { icon: 'ri-building-2-line', labelKey: 'services.fees.type3Label', durationKey: 'services.fees.type3Duration', frequencyKey: 'services.fees.type3Frequency', formatKey: 'services.fees.type3Format' },
    { icon: 'ri-focus-3-line',    labelKey: 'services.fees.type4Label', durationKey: 'services.fees.type4Duration', frequencyKey: 'services.fees.type4Frequency', formatKey: 'services.fees.type4Format' },
  ];

  return (
    <section className="bg-canvas py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-sage font-inter font-medium">{t('services.fees.badge')}</span>
          <div className="w-10 h-px bg-sage mt-3 mb-6" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2 className="font-cormorant text-4xl md:text-5xl font-light text-charcoal leading-[1.15] max-w-lg">{t('services.fees.headline')}</h2>
                      <p className="text-sm text-charcoal font-inter leading-relaxed max-w-sm">
                <Trans
                    i18nKey="services.fees.subtext"
                    components={{
                        bold: <strong />
                    }}
                />
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {sessionTypes.map((s) => (
            <SessionTypeCard key={s.labelKey} icon={s.icon} label={t(s.labelKey)} duration={t(s.durationKey)} frequency={t(s.frequencyKey)} format={t(s.formatKey)} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 bg-canvas rounded-2xl p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-information-line text-sage text-lg" />
                </div>
                <h3 className="font-inter font-semibold text-sm text-charcoal">{t('services.fees.feeNoticeTitle')}</h3>
              </div>
              <p className="font-inter text-sm text-text-muted leading-relaxed mb-5">{t('services.fees.feeNoticeText')}</p>
              <p className="font-inter text-xs text-sage leading-relaxed italic">{t('services.fees.feeNoticeNote')}</p>
            </div>
          </div>
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="bg-beige rounded-2xl p-7 flex-1 flex flex-col justify-between">
              <div className="w-7 h-7 flex items-center justify-center mb-4">
                <i className="ri-phone-line text-white/80 text-lg" />
              </div>
              <div>
                <h3 className="font-inter font-semibold text-sm text-white mb-2">{t('services.fees.introTitle')}</h3>
                <p className="font-inter text-xs text-white/65 leading-relaxed mb-5">{t('services.fees.introText')}</p>
                <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 text-xs font-inter font-medium text-white/90 hover:text-white transition-colors duration-150 cursor-pointer">
                  {t('services.fees.introBtn')} <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <div className="bg-canvas border border-sage/20 rounded-2xl p-7 flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center text-sage flex-shrink-0">
                <i className="ri-global-line text-xl" />
              </div>
              <div>
                <h3 className="font-inter font-semibold text-sm text-charcoal mb-1">{t('services.fees.onlineTitle')}</h3>
                <p className="font-inter text-xs text-text-muted leading-snug">{t('services.fees.onlineText')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
